import React from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ROLES = [
  { name: "اللون الأساسي", use: "الشعار، العناوين الرئيسية، الأزرار الأساسية والهوية العامة" },
  { name: "اللون الثانوي", use: "الخلفيات الداعمة، البطاقات، والعناصر المساندة" },
  { name: "لون التمييز (Accent)", use: "العروض، دعوات الإجراء، الأيقونات والتنبيهات المهمة" },
  { name: "لون محايد", use: "النصوص الطويلة، الخلفيات، والفواصل" },
];

const toRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return [0, 0, 0];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};
const toCmyk = ([r, g, b]) => {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const k = 1 - Math.max(R, G, B);
  if (k === 1) return [0, 0, 0, 100];
  return [(1 - R - k) / (1 - k), (1 - G - k) / (1 - k), (1 - B - k) / (1 - k), k].map((v) => Math.round(v * 100));
};
const toHsl = ([r, g, b]) => {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B), min = Math.min(R, G, B), l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === R ? (G - B) / d + (G < B ? 6 : 0) : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
    h /= 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};
const lum = (rgb) => {
  const [r, g, b] = rgb.map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (a, b) => { const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m); return ((x + 0.05) / (y + 0.05)).toFixed(1); };

const Row = ({ label, value, testid }) => (
  <button type="button" onClick={() => { navigator.clipboard?.writeText(value); toast.success(`تم نسخ ${label}`); }}
    className="flex items-center justify-between w-full border border-border rounded-lg px-3 py-2 text-sm hover:bg-secondary transition-colors" data-testid={testid}>
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono flex items-center gap-2" dir="ltr">{value} <Copy className="w-3.5 h-3.5 text-muted-foreground" /></span>
  </button>
);

export function ColorDetailDialog({ color, index, open, onOpenChange, onChangeHex }) {
  if (!color) return null;
  const rgb = toRgb(color.hex);
  const cmyk = toCmyk(rgb);
  const hsl = toHsl(rgb);
  const cw = contrast(rgb, [255, 255, 255]);
  const cb = contrast(rgb, [0, 0, 0]);
  const textOn = Number(cw) >= Number(cb) ? "#FFFFFF" : "#000000";
  const role = ROLES[index] || ROLES[3];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover border-border max-w-md" dir="rtl" data-testid="color-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-start">{color.name || "لون"} — {role.name}</DialogTitle>
          <DialogDescription className="text-start">{role.use}</DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl h-28 flex items-center justify-center border border-border text-lg font-extrabold" style={{ backgroundColor: color.hex, color: textOn }} data-testid="color-detail-preview">
          نص تجريبي على هذا اللون
        </div>
        <div className="space-y-2">
          <Row label="HEX" value={(color.hex || "").toUpperCase()} testid="color-detail-hex" />
          <Row label="RGB" value={`rgb(${rgb.join(", ")})`} testid="color-detail-rgb" />
          <Row label="CMYK (للطباعة)" value={`${cmyk.join("% ")}%`} testid="color-detail-cmyk" />
          <Row label="HSL" value={`hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`} testid="color-detail-hsl" />
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs" data-testid="color-detail-contrast">
          <div className="rounded-lg p-2 text-center font-bold" style={{ backgroundColor: color.hex, color: "#fff" }}>نص أبيض · {cw}:1 {cw >= 4.5 ? "✓" : "✗"}</div>
          <div className="rounded-lg p-2 text-center font-bold" style={{ backgroundColor: color.hex, color: "#000" }}>نص أسود · {cb}:1 {cb >= 4.5 ? "✓" : "✗"}</div>
        </div>
        <p className="text-[11px] text-muted-foreground">نسبة التباين 4.5:1 فأكثر مناسبة لقراءة النصوص (معيار WCAG). انقر على أي قيمة لنسخها.</p>
        <label className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm cursor-pointer" data-testid="color-detail-edit">
          <span>تعديل اللون</span>
          <input type="color" value={(color.hex || "#000000").slice(0, 7)} onChange={(e) => onChangeHex(index, e.target.value)} className="w-10 h-8 bg-transparent cursor-pointer" />
        </label>
      </DialogContent>
    </Dialog>
  );
}
